package com.terapia.terasenior.ui.agenda

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.ArrowForward
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.terapia.terasenior.domain.model.agenda.Appointment
import com.terapia.terasenior.domain.model.agenda.AppointmentStatus
import com.terapia.terasenior.domain.model.agenda.AppointmentType
import kotlinx.coroutines.launch
import kotlinx.datetime.*
import com.terapia.terasenior.treatment.repository.currentOrientationLocalDateTimeIso

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AgendaScreen(
    viewModel: AgendaViewModel,
    onAddAppointmentClick: (LocalDate) -> Unit,
    onAppointmentClick: (String) -> Unit
) {
    val uiState by viewModel.uiState.collectAsState()
    val diagnosticEvents by viewModel.diagnosticEvents.collectAsState()
    var showDiagnostics by remember { mutableStateOf(false) }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { 
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Text("Mi Agenda", style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold))
                        Spacer(modifier = Modifier.width(8.dp))
                        Surface(
                            color = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.5f),
                            shape = RoundedCornerShape(8.dp)
                        ) {
                            Text(
                                text = "v1.2.0",
                                modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
                                style = MaterialTheme.typography.labelSmall.copy(fontWeight = FontWeight.Bold),
                                color = MaterialTheme.colorScheme.primary
                            )
                        }
                    }
                },
                actions = {
                    IconButton(onClick = { showDiagnostics = true }) {
                        Icon(Icons.Default.BugReport, contentDescription = "Ver diagnóstico")
                    }
                    IconButton(onClick = { viewModel.loadAppointments() }) {
                        Icon(Icons.Default.Refresh, contentDescription = "Refrescar")
                    }
                }
            )
        },
        floatingActionButton = {
            FloatingActionButton(
                onClick = {
                    (uiState as? AgendaUiState.Success)?.let { state ->
                        onAddAppointmentClick(state.selectedDate)
                    }
                },
                containerColor = MaterialTheme.colorScheme.primary,
                contentColor = MaterialTheme.colorScheme.onPrimary
            ) {
                Icon(Icons.Default.Add, contentDescription = "Nueva Sesión")
            }
        }
    ) { padding ->
        Column(modifier = Modifier.fillMaxSize().padding(padding)) {
            when (val state = uiState) {
                is AgendaUiState.Loading -> {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            CircularProgressIndicator()
                            Spacer(modifier = Modifier.height(16.dp))
                            TextButton(onClick = { showDiagnostics = true }) {
                                Text("Ver diagnóstico")
                            }
                        }
                    }
                }
                is AgendaUiState.Error -> {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Text(state.message, color = MaterialTheme.colorScheme.error)
                            Spacer(modifier = Modifier.height(16.dp))
                            Button(onClick = viewModel::loadAppointments) {
                                Icon(Icons.Default.Refresh, contentDescription = null)
                                Spacer(modifier = Modifier.width(8.dp))
                                Text("Reintentar")
                            }
                        }
                    }
                }
                is AgendaUiState.Success -> {
                    DateSelector(
                        selectedDate = state.selectedDate,
                        onDateSelected = viewModel::onDateSelected
                    )

                    if (state.filteredAppointments.isEmpty()) {
                        Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                Icon(Icons.Default.EventBusy, contentDescription = null, modifier = Modifier.size(64.dp), tint = Color.LightGray)
                                Spacer(modifier = Modifier.height(16.dp))
                                Text("No hay sesiones para este día", color = Color.Gray)
                            }
                        }
                    } else {
                        LazyColumn(
                            contentPadding = PaddingValues(16.dp),
                            verticalArrangement = Arrangement.spacedBy(12.dp),
                            modifier = Modifier.fillMaxSize()
                        ) {
                            items(state.filteredAppointments) { pair ->
                                AppointmentCard(
                                    appointment = pair.first,
                                    attendeeNames = pair.second,
                                    onClick = { onAppointmentClick(pair.first.id) }
                                )
                            }
                        }
                    }
                }
            }
        }
    }

    if (showDiagnostics) {
        AlertDialog(
            onDismissRequest = { showDiagnostics = false },
            title = { Text("Diagnóstico de Agenda") },
            text = {
                if (diagnosticEvents.isEmpty()) {
                    Text("Aún no hay eventos registrados.")
                } else {
                    LazyColumn(
                        modifier = Modifier.heightIn(max = 320.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        items(diagnosticEvents) { event ->
                            Text(event, style = MaterialTheme.typography.bodySmall)
                        }
                    }
                }
            },
            confirmButton = {
                TextButton(onClick = { showDiagnostics = false }) { Text("Cerrar") }
            }
        )
    }
}

@OptIn(kotlin.time.ExperimentalTime::class)
@Composable
private fun DateSelector(
    selectedDate: LocalDate,
    onDateSelected: (LocalDate) -> Unit
) {
    val today = remember {
        agendaTodayForSelector()
    }
    
    val listState = rememberLazyListState()
    val scope = rememberCoroutineScope()
    
    // Generamos un rango amplio de días para navegar
    val days = remember {
        (-15..45).map { today.plus(it, DateTimeUnit.DAY) }
    }

    // Scroll automático al día seleccionado al iniciar
    LaunchedEffect(Unit) {
        val index = days.indexOf(selectedDate)
        if (index != -1) {
            listState.scrollToItem(maxOf(0, index - 2))
        }
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f))
            .padding(vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        IconButton(onClick = {
            scope.launch {
                listState.animateScrollToItem(maxOf(0, listState.firstVisibleItemIndex - 4))
            }
        }) {
            Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Anterior")
        }

        LazyRow(
            state = listState,
            modifier = Modifier.weight(1f),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            contentPadding = PaddingValues(horizontal = 4.dp)
        ) {
            items(days) { date ->
                val isSelected = date == selectedDate
                val isToday = date == today
                val isWeekend = date.dayOfWeek == DayOfWeek.SATURDAY || date.dayOfWeek == DayOfWeek.SUNDAY
                
                Card(
                    modifier = Modifier
                        .width(60.dp)
                        .height(80.dp)
                        .clickable { onDateSelected(date) },
                    colors = CardDefaults.cardColors(
                        containerColor = when {
                            isSelected -> MaterialTheme.colorScheme.primary
                            isWeekend -> Color(0xFFFFEBEE)
                            else -> MaterialTheme.colorScheme.surface
                        },
                        contentColor = when {
                            isSelected -> MaterialTheme.colorScheme.onPrimary
                            isWeekend -> Color.Red
                            else -> MaterialTheme.colorScheme.onSurface
                        }
                    ),
                    shape = RoundedCornerShape(12.dp),
                    elevation = CardDefaults.cardElevation(defaultElevation = if (isSelected) 4.dp else 1.dp)
                ) {
                    Column(
                        modifier = Modifier.fillMaxSize(),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.Center
                    ) {
                        val dayLabel = when(date.dayOfWeek) {
                            DayOfWeek.MONDAY -> "Lun"
                            DayOfWeek.TUESDAY -> "Mar"
                            DayOfWeek.WEDNESDAY -> "Mié"
                            DayOfWeek.THURSDAY -> "Jue"
                            DayOfWeek.FRIDAY -> "Vie"
                            DayOfWeek.SATURDAY -> "Sáb"
                            DayOfWeek.SUNDAY -> "Dom"
                        }
                        Text(
                            text = dayLabel,
                            style = MaterialTheme.typography.labelSmall,
                            fontWeight = if (isToday) FontWeight.ExtraBold else FontWeight.Normal
                        )
                        Text(
                            text = date.dayOfMonth.toString(),
                            style = MaterialTheme.typography.titleLarge,
                            fontWeight = FontWeight.Bold
                        )
                        if (isToday) {
                            Box(modifier = Modifier.size(4.dp).clip(CircleShape).background(if(isSelected) Color.White else MaterialTheme.colorScheme.primary))
                        }
                    }
                }
            }
        }

        IconButton(onClick = {
            scope.launch {
                listState.animateScrollToItem(minOf(days.size - 1, listState.firstVisibleItemIndex + 4))
            }
        }) {
            Icon(Icons.AutoMirrored.Filled.ArrowForward, contentDescription = "Siguiente")
        }
    }
}

private fun agendaTodayForSelector(): LocalDate = try {
    LocalDateTime.parse(currentOrientationLocalDateTimeIso()).date
} catch (_: Throwable) {
    LocalDate(2026, 9, 18)
}

@Composable
private fun AppointmentCard(
    appointment: Appointment,
    attendeeNames: List<String>,
    onClick: () -> Unit
) {
    val statusColor = when(appointment.status) {
        AppointmentStatus.SCHEDULED -> MaterialTheme.colorScheme.primary
        AppointmentStatus.CONFIRMED -> Color(0xFF2E7D32)
        AppointmentStatus.IN_PROGRESS -> Color(0xFFF57C00)
        AppointmentStatus.COMPLETED -> Color(0xFF2E7D32)
        AppointmentStatus.CANCELLED -> MaterialTheme.colorScheme.error
        AppointmentStatus.MISSED -> Color.Gray
    }

    Card(
        onClick = onClick,
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Row(
            modifier = Modifier.padding(16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier = Modifier.width(50.dp)
            ) {
                val startDateTime = kotlinx.datetime.Instant.parse(appointment.startAt).toLocalDateTime(TimeZone.UTC)
                val endDateTime = kotlinx.datetime.Instant.parse(appointment.endAt).toLocalDateTime(TimeZone.UTC)
                
                Text(
                    text = "${startDateTime.hour.toString().padStart(2, '0')}:${startDateTime.minute.toString().padStart(2, '0')}",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold
                )
                Text(
                    text = "${endDateTime.hour.toString().padStart(2, '0')}:${endDateTime.minute.toString().padStart(2, '0')}",
                    style = MaterialTheme.typography.labelSmall,
                    color = Color.Gray
                )
            }

            Spacer(modifier = Modifier.width(16.dp))
            Box(modifier = Modifier.width(4.dp).height(50.dp).background(statusColor, RoundedCornerShape(2.dp)))
            Spacer(modifier = Modifier.width(16.dp))

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = appointment.title,
                    style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                    maxLines = 1
                )
                if (attendeeNames.isNotEmpty()) {
                    Text(
                        text = "Pacientes: ${attendeeNames.joinToString(", ")}",
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.secondary,
                        maxLines = 1
                    )
                } else {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        val icon = if (appointment.type == AppointmentType.INDIVIDUAL) Icons.Default.Person else Icons.Default.Groups
                        Icon(icon, contentDescription = null, modifier = Modifier.size(14.dp), tint = Color.Gray)
                        Spacer(modifier = Modifier.width(4.dp))
                        val typeLabel = if (appointment.type == AppointmentType.INDIVIDUAL) "Sesión Individual" else "Taller Grupal"
                        Text(
                            text = typeLabel,
                            style = MaterialTheme.typography.bodySmall,
                            color = Color.Gray
                        )
                    }
                }
            }

            Surface(
                color = statusColor.copy(alpha = 0.1f),
                shape = RoundedCornerShape(8.dp)
            ) {
                val label = when(appointment.status) {
                    AppointmentStatus.SCHEDULED -> "Pendiente"
                    AppointmentStatus.CONFIRMED -> "Confirmada"
                    AppointmentStatus.IN_PROGRESS -> "En curso"
                    AppointmentStatus.COMPLETED -> "Finalizada"
                    AppointmentStatus.CANCELLED -> "Cancelada"
                    AppointmentStatus.MISSED -> "No presentado"
                }
                Text(
                    text = label,
                    modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
                    style = MaterialTheme.typography.labelSmall.copy(fontWeight = FontWeight.Bold),
                    color = statusColor
                )
            }
        }
    }
}
